import { useCallback, useState } from "react";
import type { SitePerformanceData, DateRange } from "../types/dashboard.types";
import type { Site } from "../../groups/types/Emailstypes";
 

const fmt = (n: number) => String(n);
export const useDashboardExport = ({
  site,
  data,
  compareData,
  dateRange,
  compareRange,
  compareEnabled,
}: {
  site: Site;
  data?: SitePerformanceData;
  compareData?: SitePerformanceData;
  dateRange: DateRange;
  compareRange: DateRange;
  compareEnabled: boolean;
}) => {
  const [exporting, setExporting] = useState(false);

   const exportCSV = useCallback(() => {
    if (!data) return;
    setExporting(true);

    try {
      const rows: string[][] = [];
       const headers = ["Fiche", "Impressions", "Appels", "Clics web", "Itinéraires", "Réservations"];
      if (compareEnabled) {
        headers.push(
          "Impressions (comp.)", "Appels (comp.)",
          "Clics web (comp.)", "Itinéraires (comp.)"
        );
      }
      rows.push(headers);

       const locations = data.perLocation ?? [];
      const compareMap: Record<string, typeof locations[0]> = {};
      if (compareEnabled && compareData?.perLocation) {
        compareData.perLocation.forEach(l => { compareMap[l.locationName] = l; });
      }

      locations.forEach(loc => {
        const k = loc.kpis;
        const row = [
          loc.title ?? loc.locationName,
          fmt(k?.totalImpressions ?? 0),
          fmt(k?.callClicks ?? 0),
          fmt(k?.websiteClicks ?? 0),
          fmt(k?.directionRequests ?? 0),
          fmt(k?.bookings ?? 0),
        ];
        if (compareEnabled) {
          const ck = compareMap[loc.locationName]?.kpis;
          row.push(
            fmt(ck?.totalImpressions ?? 0),
            fmt(ck?.callClicks ?? 0),
            fmt(ck?.websiteClicks ?? 0),
            fmt(ck?.directionRequests ?? 0),
          );
        }
        rows.push(row);
      });

      const k = data.kpis;
      const totalRow = [
        "TOTAL",
        fmt(k?.totalImpressions ?? 0),
        fmt(k?.callClicks ?? 0),
        fmt(k?.websiteClicks ?? 0),
        fmt(k?.directionRequests ?? 0),
        fmt(k?.bookings ?? 0),
      ];
      if (compareEnabled && compareData?.kpis) {
        const ck = compareData.kpis;
        totalRow.push(
          fmt(ck.totalImpressions ?? 0),
          fmt(ck.callClicks ?? 0),
          fmt(ck.websiteClicks ?? 0),
          fmt(ck.directionRequests ?? 0),
        );
      }
      rows.push([], totalRow);

      const csv = rows.map(r => r.map(c => `"${c}"`).join(",")).join("\n");
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `${site.id}_${dateRange.startDate}_${dateRange.endDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }, [data, compareData, compareEnabled, site, dateRange]);

   const exportPDF = useCallback(async () => {
    if (!data) return;
    setExporting(true);

    try {
      const { default: jsPDF } = await import("jspdf");
      const { default: autoTable } = await import("jspdf-autotable");

      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const blue  = [15, 82, 186] as [number, number, number];
      const light = [248, 250, 252] as [number, number, number];

      doc.setFillColor(...blue);
      doc.rect(0, 0, 297, 22, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(site.label, 12, 10);
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
   doc.text(
  `Performances · ${dateRange.startDate} → ${dateRange.endDate}` +
  (compareEnabled ? `  |  Comparaison : ${compareRange.startDate} → ${compareRange.endDate}` : ""),
  12, 17
);

       const k = data.kpis;
      const ck = compareEnabled ? compareData?.kpis : null;
 
      const delta = (cur: number, prev?: number) => {
        if (!prev || prev === 0) return "";
        const pct = Math.round(((cur - prev) / prev) * 100);
        return pct > 0 ? `+${pct}%` : `${pct}%`;
      };

      doc.setTextColor(30, 41, 59);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("KPIs globaux", 12, 30);

      const kpiData = [
        ["Impressions",  fmt(k?.totalImpressions ?? 0),  ck ? fmt(ck.totalImpressions ?? 0) : "—", delta(k?.totalImpressions ?? 0, ck?.totalImpressions)],
        ["Appels",       fmt(k?.callClicks ?? 0),         ck ? fmt(ck.callClicks ?? 0)        : "—", delta(k?.callClicks ?? 0, ck?.callClicks)],
        ["Clics web",    fmt(k?.websiteClicks ?? 0),      ck ? fmt(ck.websiteClicks ?? 0)     : "—", delta(k?.websiteClicks ?? 0, ck?.websiteClicks)],
        ["Itinéraires",  fmt(k?.directionRequests ?? 0),  ck ? fmt(ck.directionRequests ?? 0) : "—", delta(k?.directionRequests ?? 0, ck?.directionRequests)],
      ];

      const kpiHead = compareEnabled
        ? [["Métrique", "Période principale", "Période comparaison", "Δ"]]
        : [["Métrique", "Valeur"]];

      const kpiBody = compareEnabled
        ? kpiData
        : kpiData.map(r => [r[0], r[1]]);

      autoTable(doc, {
        startY: 34,
        head: kpiHead,
        body: kpiBody,
        theme: "grid",
        headStyles:  { fillColor: blue, textColor: 255, fontSize: 9, fontStyle: "bold" },
        bodyStyles:  { fontSize: 9, textColor: [30, 41, 59] },
        alternateRowStyles: { fillColor: light },
        columnStyles: { 3: { textColor: [16, 185, 129] } },
        margin: { left: 12, right: 12 },
      });

       const finalY = (doc as any).lastAutoTable?.finalY ?? 70;
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(30, 41, 59);
      doc.text("Détail par fiche", 12, finalY + 10);
const locations = data?.perLocation ?? [];
const compareMap: Record<string, typeof locations[number]> = {};
       if (compareEnabled && compareData?.perLocation) {
        compareData.perLocation.forEach(l => { compareMap[l.locationName] = l; });
      }

      const locHead = compareEnabled
        ? [["Fiche", "Impr.", "Appels", "Web", "Itin.", "Impr. (C)", "Appels (C)", "Web (C)", "Δ Impr."]]
        : [["Fiche", "Impressions", "Appels", "Clics web", "Itinéraires", "Réservations"]];

      const locBody = (data.perLocation ?? []).map(loc => {
        const lk = loc.kpis;
        const ck2 = compareMap[loc.locationName]?.kpis;
        const d = delta(lk?.totalImpressions ?? 0, ck2?.totalImpressions);
        if (compareEnabled) {
          return [
            loc.title ?? loc.locationName,
            fmt(lk?.totalImpressions ?? 0),
            fmt(lk?.callClicks ?? 0),
            fmt(lk?.websiteClicks ?? 0),
            fmt(lk?.directionRequests ?? 0),
            ck2 ? fmt(ck2.totalImpressions ?? 0) : "—",
            ck2 ? fmt(ck2.callClicks ?? 0)        : "—",
            ck2 ? fmt(ck2.websiteClicks ?? 0)     : "—",
            d,
          ];
        }
        return [
          loc.title ?? loc.locationName,
          fmt(lk?.totalImpressions ?? 0),
          fmt(lk?.callClicks ?? 0),
          fmt(lk?.websiteClicks ?? 0),
          fmt(lk?.directionRequests ?? 0),
          fmt(lk?.bookings ?? 0),
        ];
      });

      autoTable(doc, {
        startY: finalY + 14,
        head: locHead,
        body: locBody,
        theme: "striped",
        headStyles:  { fillColor: blue, textColor: 255, fontSize: 8, fontStyle: "bold" },
        bodyStyles:  { fontSize: 8, textColor: [30, 41, 59] },
        alternateRowStyles: { fillColor: light },
        margin: { left: 12, right: 12 },
      });

       const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(148, 163, 184);
        doc.text(
          `${site.label} · Généré le ${new Date().toLocaleDateString("fr-FR")} · Page ${i}/${pageCount}`,
          12, doc.internal.pageSize.height - 5
        );
      }

      doc.save(`${site.id}_${dateRange.startDate}_${dateRange.endDate}.pdf`);
    } finally {
      setExporting(false);
    }
  }, [data, compareData, compareEnabled, site, dateRange, compareRange]);

  return { exporting, exportCSV, exportPDF };
};